import { format, parseISO } from 'date-fns';

/**
 * Frontend Timezone Utilities
 *
 * Our strategy: Store in UTC, Display in Local Timezone.
 * These utilities help convert UTC strings from the backend into
 * motel-local time strings for display.
 */

/**
 * Format a UTC date string to a motel's local time string
 *
 * @param date The date string or Date object (assumed UTC if string)
 * @param timezone The IANA timezone identifier (e.g., 'America/New_York')
 * @param formatStr The desired format (compatible with date-fns or custom)
 * @returns Formatted date string in local time
 */
export function formatInTimezone(
    date: string | Date | number,
    timezone: string = 'UTC',
    formatStr: string = 'PPpp',
): string {
    if (!date) return '';

    // NOTE: The YYYY-MM-DD early-return that was here has been removed.
    // Calendar dates (start_date, end_date) should NOT go through formatInTimezone().
    // Use format(parseISO(date), formatStr) at the call site instead.
    // Timestamps (created_at, etc.) should flow through the Intl conversion below.
    const d = typeof date === 'string' ? parseISO(date) : new Date(date);

    // If the date is invalid, return the original input as a string or empty
    if (isNaN(d.getTime())) {
        console.warn(`[window.Time] Invalid date value: ${date}`);
        return typeof date === 'string' ? date : '';
    }

    // Use Intl to get the local time components
    const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric',
        hourCycle: 'h23',
    });

    const parts = formatter.formatToParts(d);
    const partValues: Record<string, string> = {};
    parts.forEach((part) => {
        partValues[part.type] = part.value;
    });

    // Create a Date object that represents the same wall-clock time in LOCAL time
    // This allows us to use date-fns formatting on it without the browser's
    // local timezone shifting it again.
    const localDate = new Date(
        parseInt(partValues.year),
        parseInt(partValues.month) - 1,
        parseInt(partValues.day),
        parseInt(partValues.hour) % 24,
        parseInt(partValues.minute),
        parseInt(partValues.second),
    );

    return format(localDate, formatStr);
}

/**
 * Get "Today" as a Date object relative to a motel's timezone.
 *
 * IMPORTANT: The returned Date represents midnight of today in the motel's
 * local timezone. It is safe for date arithmetic and comparisons only.
 *
 * @warning Do NOT pass the result of this function into formatInTimezone()
 * as it will result in double-offsetting.
 *
 * For display use: format(getMotelToday(timezone), 'MMM d, yyyy')
 * For comparison use: getMotelToday(timezone) >= someOtherDate
 */
/**
 * Returns today as a Date in the given motel timezone.
 * Safe for DATE COMPARISONS only.
 *
 * DO NOT pass the result to formatInTimezone() — the browser
 * will re-interpret it in its own timezone and shift the date.
 *
 * For display: format(getMotelToday(tz), 'MMM d, yyyy')
 * For comparison: getMotelToday(tz) >= someOtherDate
 */
export function getMotelToday(timezone: string): Date {
    const now = new Date();
    const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
    });

    const parts = formatter.formatToParts(now);
    const v: Record<string, string> = {};
    parts.forEach((p) => {
        v[p.type] = p.value;
    });

    return new Date(parseInt(v.year), parseInt(v.month) - 1, parseInt(v.day));
}

/**
 * Get formatted current time for a motel
 */
export function getMotelNowFormatted(
    timezone: string,
    formatStr: string = 'HH:mm',
): string {
    return formatInTimezone(new Date(), timezone, formatStr);
}
